import {Button, InputField, Textarea} from '@nlabs/gothamjs/components';
import {Check, ChevronDown, Download, Image, Save, Sparkles, Video, X} from '@nlabs/gothamjs/icons';
import {estimateGenerationCredits, formatCredits, PLAN_LIMITS} from '@reactorbox/shared';
import {useState} from 'react';

import {createRequestId} from '../../utils/createRequestId.js';

import type {Project, Shot, Workspace} from '@reactorbox/shared';
import type {FormEvent, ReactElement} from 'react';
import type {WorkspaceActions} from '../../state/workspaceActions.js';

interface ShotEditorProps {
  readonly actions: WorkspaceActions;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly project: Project;
  readonly shot?: Shot;
  readonly workspace: Workspace;
}

const inspectorInput = 'w-full !rounded-xl !border !border-black/10 !bg-white/30 !px-3.5 !py-2.5 !text-[12px] !leading-relaxed !text-[#302936] !outline-none placeholder:!text-[#302936]/35 focus:!border-black/25 focus:!bg-white/50 focus:!ring-2 focus:!ring-black/5';
const inspectorLabel = 'mb-2 flex items-center justify-between gap-2 text-[10px] font-semibold text-[#302936]/65';
const inspectorButton = '!inline-flex !min-h-10 !items-center !justify-center !gap-2 !rounded-full border-sky-800/20! bg-sky-100/60! !px-4 !py-2.5 !text-[11px] !font-medium !text-[#302936] hover:bg-sky-100/85! disabled:!opacity-35';
const smallIconButton = '!inline-flex !size-8 !min-h-8 !min-w-8 !items-center !justify-center !rounded-full !border-black/10 !bg-transparent !p-0 !text-[#302936]/55 hover:!bg-white/40';

export const ShotEditor = ({actions, busy, onClose, project, shot, workspace}: ShotEditorProps): ReactElement => {
  const [elementIds, setElementIds] = useState(shot?.elementIds || []);
  const [placement, setPlacement] = useState(shot?.placement || '');
  const [title, setTitle] = useState(shot?.title || '');
  const [description, setDescription] = useState(shot?.description || '');
  const [duration, setDuration] = useState(shot?.duration || 5);
  const [resolution, setResolution] = useState(shot?.resolution || '720p');
  const [referenceUrl, setReferenceUrl] = useState(shot?.referenceUrl || '');
  const [characterIds, setCharacterIds] = useState(shot?.characterIds || []);
  const assets = workspace.assets.filter((asset) => asset.shotId === shot?.id);
  const jobs = workspace.jobs.filter((job) => job.shotId === shot?.id);
  const activeJob = jobs.find((job) => !['succeeded', 'failed'].includes(job.status));
  const validDuration = Number.isInteger(duration) && duration >= 4 && duration <= 30;
  const dirty = shot && (elementIds.join() !== (shot.elementIds || []).join() || placement !== (shot.placement || '') || title !== shot.title || description !== shot.description || duration !== shot.duration || resolution !== shot.resolution || referenceUrl !== (shot.referenceUrl || '') || characterIds.join() !== shot.characterIds.join());
  const imagePrice = shot ? estimateGenerationCredits('image', shot.duration, workspace.pricing) : 0;
  const videoPrice = shot ? estimateGenerationCredits('video', shot.duration, workspace.pricing) : 0;
  const unavailable = busy || Boolean(activeJob) || Boolean(dirty);
  const save = (event: FormEvent): void => {
    event.preventDefault();
    const body = {
      characterIds,
      description: description.trim(),
      duration,
      elementIds,
      placement,
      referenceUrl: referenceUrl.trim(),
      resolution,
      title: title.trim()
    };
    void actions.mutate(shot ? `/api/shots/${shot.id}` : `/api/projects/${project.id}/shots`, body, shot ? 'Shot saved.' : 'Shot added to your storyboard.', shot ? 'PATCH' : 'POST');
  };
  const generate = (kind: 'image' | 'video'): void => {
    if(!shot) {
      return;
    }
    void actions.mutate('/api/generations', {idempotencyKey: createRequestId(), kind, projectId: project.id, shotId: shot.id}, `${workspace.mode === 'demo' ? 'Preview' : 'Generation'} job queued. You can keep working.`);
  };

  return (
    <aside
      aria-label="Shot details"
      className="relative isolate overflow-hidden rounded-[28px] border border-white/30 bg-linear-to-b from-[#d9d0f7] via-[#b9b6ee] to-[#72c9ed] p-5 text-[#302936] shadow-[0_20px_80px_-40px_rgba(214,182,229,0.5)] sm:p-6 xl:mt-0"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 -z-10 size-64 rounded-full bg-white/35 blur-3xl" />
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[8px] font-semibold tracking-[0.17em] text-[#65566e]/60">
            <Sparkles size={11} />YOUR NEXT FRAME
          </span>
          <h2 className="mt-2 text-[21px] font-medium tracking-[-0.035em] text-violet-950">{shot ? 'Shot details' : 'Add a shot'}</h2>
        </div>
        <Button aria-label="Close shot details" className={smallIconButton} onClick={onClose} variant="text"><X size={16} /></Button>
      </header>
      <form className="space-y-4" onSubmit={save}>
        <div>
          <label className={inspectorLabel} htmlFor="shot-title">Shot title</label>
          <InputField
            aria-label="Shot title"
            autoFocus={!shot}
            borderType="none"
            className={inspectorInput}
            id="shot-title"
            maxLength={120}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="A light in the distance"
            required
            value={title}
          />
        </div>
        <div>
          <label className={inspectorLabel} htmlFor="shot-description">Shot description</label>
          <Textarea
            className={`${inspectorInput} !min-h-[136px] !resize-y`}
            id="shot-description"
            maxLength={4000}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Describe the scene. The movement, the light, the way it should feel…"
            rows={5}
            value={description}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={inspectorLabel} htmlFor="shot-duration">Duration (seconds)</label>
            <InputField
              borderType="none"
              className={`${inspectorInput} !rounded-full`}
              id="shot-duration"
              max={30}
              min={4}
              onChange={(event) => setDuration(Number(event.target.value))}
              required
              step={1}
              type="number"
              value={duration}
            />
          </div>
          <div>
            <label className={inspectorLabel} htmlFor="shot-resolution">Resolution</label>
            <div className="relative">
              <select
                className={`${inspectorInput} !rounded-full appearance-none !pr-8`}
                id="shot-resolution"
                onChange={(event) => setResolution(event.target.value as Shot['resolution'])}
                value={resolution}
              >
                <option className="bg-[#ecdfed] text-[#302936]">720p</option>
                <option className="bg-[#ecdfed] text-[#302936]">480p</option>
              </select>
              <ChevronDown aria-hidden="true" className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-black/40" size={13} />
            </div>
          </div>
        </div>
        <div>
          <label className={inspectorLabel} htmlFor="shot-reference">
            Image reference URL <span className="text-[9px] font-normal text-[#302936]/35">Optional</span>
          </label>
          <InputField
            borderType="none"
            className={inspectorInput}
            id="shot-reference"
            onChange={(event) => setReferenceUrl(event.target.value)}
            placeholder="https://…"
            type="url"
            value={referenceUrl}
          />
        </div>
        {project.characters.length > 0 && (
          <fieldset>
            <legend className={inspectorLabel}>Characters in this shot</legend>
            <div className="flex flex-wrap gap-2">
              {project.characters.map((character) => (
                <label
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-2 text-[10px] transition ${characterIds.includes(character.id) ? 'border-black/15 bg-white/55 text-[#302936]' : 'border-black/10 bg-white/20 text-[#302936]/55'}`}
                  key={character.id}
                >
                  <input
                    checked={characterIds.includes(character.id)}
                    className="size-3 accent-violet-600"
                    onChange={(event) => setCharacterIds(event.target.checked
                      ? [...characterIds, character.id]
                      : characterIds.filter((id) => id !== character.id))}
                    type="checkbox"
                  />
                  {character.name}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {Boolean(workspace.elements?.length) && <fieldset>
          <legend className={inspectorLabel}>Models & products in this shot</legend>
          <div className="space-y-2">{workspace.elements!.map((element) => <label className="flex items-center gap-2 text-xs" key={element.id}><input checked={elementIds.includes(element.id)} onChange={(event) => setElementIds(event.target.checked ? [...elementIds, element.id] : elementIds.filter((id) => id !== element.id))} type="checkbox" />{element.name} · {element.kind}</label>)}</div>
          <label className={`${inspectorLabel} mt-3`}>Placement direction<Textarea className={`${inspectorInput} mt-2`} maxLength={2000} onChange={(event) => setPlacement(event.target.value)} placeholder="Where is the product, who holds it, and how should it appear?" rows={3} value={placement} /></label>
        </fieldset>}
        <Button
          className={`${inspectorButton} w-full`}
          disabled={busy || !title.trim() || !validDuration}
          icon={<Save size={13} />}
          type="submit"
          variant="outline"
        >
          {shot ? 'Save changes' : 'Save shot'}
        </Button>
      </form>
      {shot && (
        <section className="mt-6 border-t border-black/10 pt-5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-[12px] font-semibold">Bring it to life</h3>
            <span className="text-[9px] text-[#302936]/40">{formatCredits(workspace.credits.available)} available</span>
          </div>
          <p className="mt-1.5 text-[10px] text-[#302936]/45">
            {workspace.plan === 'free' ? 'Free accounts can write and save shots. Upgrade to generate images or videos.' : 'Credits are reserved before generation.'}
          </p>
          {dirty && <p className="mt-3 rounded-xl bg-white/35 px-3 py-2 text-[10px] text-[#685023]">Save your changes before generating.</p>}
          <div className="mt-4 space-y-2.5">
            <Button
              className={`${inspectorButton} w-full [&>span]:w-full`}
              disabled={workspace.plan === 'free' || unavailable || workspace.credits.available < imagePrice}
              onClick={() => generate('image')}
              variant="outline"
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2"><Image size={14} />{workspace.mode === 'demo' ? 'Generate preview image' : 'Generate image'}</span>
                <strong className="text-[10px] font-semibold">{formatCredits(imagePrice)}</strong>
              </span>
            </Button>
            <Button
              className="!inline-flex !min-h-11 w-full !items-center !justify-center !rounded-full !border-0 bg-violet-600! !px-4 !py-3 !text-[11px] !font-medium !text-white hover:bg-violet-700! disabled:!opacity-35 [&>span]:w-full"
              disabled={workspace.plan === 'free' || unavailable || workspace.credits.available < videoPrice}
              onClick={() => generate('video')}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2"><Video size={14} />{workspace.mode === 'demo' ? 'Generate preview video' : 'Generate video'}</span>
                <strong className="text-[10px] font-semibold text-white/65">{formatCredits(videoPrice)}</strong>
              </span>
            </Button>
          </div>
          <p className="mt-3 text-[9px] leading-relaxed text-[#302936]/40">
            {workspace.mode === 'demo'
              ? 'Video previews simulate the workflow; no playable movie is generated.'
              : 'Completed attempts consume credits even when not approved.'}
          </p>
          {activeJob && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-black/10 bg-white/30 p-3 text-[10px] leading-relaxed text-[#302936]/60" role="status">
              <span className="mt-1 size-1.5 shrink-0 rounded-full bg-[#796285]" />
              {activeJob.status === 'reconciliation_required' ? 'Outcome pending review — credits remain reserved.' : `Job ${activeJob.status}. Your work is saved.`}
            </div>
          )}
          {jobs.filter((job) => job.status === 'failed').slice(-1).map((job) => (
            <p className="mt-3 rounded-xl bg-[#fff5e6]/60 p-3 text-[10px] leading-relaxed text-[#825735]" key={job.id}>
              Generation failed: {job.error || 'Credits released. Try another shot.'}
            </p>
          ))}
        </section>
      )}
      {assets.length > 0 && (
        <section className="mt-5 border-t border-black/10 pt-5">
          <h3 className="flex items-center gap-2 text-[12px] font-semibold">
            Takes <span className="rounded-full bg-white/40 px-1.5 py-0.5 text-[9px] font-normal">{assets.length}</span>
          </h3>
          <div className="mt-3 space-y-2">
            {assets.map((asset, index) => (
              <div className="flex items-center gap-2.5 rounded-2xl border border-black/[0.06] bg-white/20 p-2.5" key={asset.id}>
                <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-white/35 text-[#302936]/50">
                  {asset.kind === 'image' ? <Image size={15} /> : <Video size={15} />}
                </span>
                <div className="min-w-0 flex-1">
                  <strong className="block text-[10px] font-medium">Take {index + 1} · {asset.kind}</strong>
                  <span className="mt-0.5 block text-[9px] text-[#302936]/40">{asset.isDemo ? 'Local preview' : 'Generated asset'}</span>
                </div>
                {asset.mediaUrl && (
                  <a
                    aria-label={`Download take ${index + 1}`}
                    className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[#302936]/55 transition hover:bg-white/50"
                    download
                    href={asset.mediaUrl}
                  >
                    <Download size={13} />
                  </a>
                )}
                <Button
                  aria-label={shot?.approvedAssetId === asset.id ? `Take ${index + 1} approved` : `Approve take ${index + 1}`}
                  className={`${smallIconButton} ${shot?.approvedAssetId === asset.id ? '!border-[#659877]/20 !bg-[#dbf0dd]/70 !text-[#3d7150]' : ''}`}
                  disabled={busy || shot?.approvedAssetId === asset.id ||
                    Boolean(workspace.plan && !PLAN_LIMITS[workspace.plan].approvals)}
                  onClick={() => void actions.mutate(`/api/shots/${shot!.id}/approve`, {assetId: asset.id}, 'Take approved.')}
                  title={workspace.plan && !PLAN_LIMITS[workspace.plan].approvals
                    ? 'Approvals require Creator or Studio' : 'Approve take'}
                  variant="outline"
                >
                  <Check size={13} />
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
    </aside>
  );
};
